export type ArticleSummaryPlacement = "top" | "after-hero";

export type TldrSourceReference = {
  title: string;
  url: string;
};

/**
 * These two source-qualified agent-commerce articles need their AI-generated,
 * source-checked takeaways above the hero, where a time-poor commercial reader
 * can decide whether to continue before entering the long-form analysis.
 */
const TOP_TLDR_ARTICLE_SLUGS = new Set([
  "retailer-agent-access-policy-ai-shopping-2026",
  "ai-agents-consumer-inertia-margin-resilience-2026",
]);

/**
 * Each number is the approved source-context index for the matching summary
 * bullet. `null` marks editorial synthesis or guidance: it deliberately
 * renders without a citation rather than presenting an unqualified
 * bibliography as proof.
 */
const TLDR_SOURCE_INDEX_BY_BULLET: Readonly<Record<string, readonly (number | null)[]>> = {
  "retailer-agent-access-policy-ai-shopping-2026": [0, 1, 1, 0],
  "ai-agents-consumer-inertia-margin-resilience-2026": [0, 1, 1, 0],
  "gemini-4-argon-frontier-model-cybersecurity-governance-2026": [0, 0, 0, null],
  "instinct-personal-agent-trust-alignment-2026": [null, 1, 2, null, null],
  "america-gov-phase-1-launch-gemini-and-phase-2": [2, null, null, null],
  "cue-by-manus-personal-agent-team-governance-2026": [0, null, null, null],
  "openai-gpt-6-1-astra-safety-governance-2026": [0, 0, null, null],
  "openai-70bn-revenue-run-rate-ipo-economics-2026": [0, null, null, null, null],
};

export function getArticleSummaryPlacement(slug: string): ArticleSummaryPlacement {
  return TOP_TLDR_ARTICLE_SLUGS.has(slug) ? "top" : "after-hero";
}

/**
 * Resolves only an explicitly mapped, article-level source. An absent or null
 * mapping renders no link, rather than guessing at a source for an editorial
 * takeaway.
 */
export function getTldrBulletSource(
  slug: string,
  bulletIndex: number,
  sources: readonly TldrSourceReference[],
): TldrSourceReference | undefined {
  const sourceIndex = TLDR_SOURCE_INDEX_BY_BULLET[slug]?.[bulletIndex];
  return typeof sourceIndex === "number" ? sources[sourceIndex] : undefined;
}
